import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WHOLESALE_METHOD_LABELS,
  WHOLESALE_STAGE_LABELS,
  WHOLESALE_TARGET_LABELS,
  type WholesaleCashDTO,
  type WholesaleHolderDTO,
  type WholesaleSettlementDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { HandCoins, Landmark, Wallet } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { moneyInput, parseMoney, useWsSettings } from './common';
import './wholesale.css';

/** Lo que tiene cada repartidor o vendedor, las rendiciones y la caja mayorista. */
export function WholesaleMoneyPage() {
  const { can, me } = useSession();
  const manage = can('wholesale.manage');
  const settings = useWsSettings();
  const holders = useQuery({ queryKey: ['wholesale', 'holders'], queryFn: () => api.get<WholesaleHolderDTO[]>('/wholesale/holders') });
  const settlements = useQuery({
    queryKey: ['wholesale', 'settlements'],
    queryFn: () => api.get<WholesaleSettlementDTO[]>('/wholesale/settlements'),
  });
  const cash = useQuery({ queryKey: ['wholesale', 'cash'], queryFn: () => api.get<WholesaleCashDTO>('/wholesale/cash'), enabled: manage });
  const [settling, setSettling] = useState<WholesaleHolderDTO | null>(null);
  const [cashMode, setCashMode] = useState<'WITHDRAWAL' | 'DEPOSIT' | 'COUNT' | null>(null);
  const target = settings.data?.settlementTarget ?? 'WHOLESALE_CASH';
  const branchName = me.branches.find((b) => b.id === settings.data?.settlementBranchId)?.name;
  const inStreet = (holders.data ?? []).reduce((t, h) => t + h.cashCents, 0);

  return (
    <>
      <PageHeader
        title="Cobros y rendición"
        subtitle={
          target === 'BRANCH_CASH'
            ? `Lo que cobran los repartidores y vendedores se rinde y entra a la caja de ${branchName ?? 'la sucursal elegida'}, identificado como mayorista.`
            : 'Lo que cobran los repartidores y vendedores se rinde y entra a la caja mayorista.'
        }
      />
      <div className="bf-kpi-grid">
        <Kpi label="Efectivo en la calle" tone="amber" icon={<HandCoins />} value={formatMoney(inStreet)} delta="Cobrado y sin rendir" deltaMuted />
        {manage && target === 'WHOLESALE_CASH' && (
          <Kpi
            label="Caja mayorista"
            tone="green"
            icon={<Wallet />}
            value={cash.data ? formatMoney(cash.data.balanceCents) : '—'}
            delta="Saldo en efectivo"
            deltaMuted
          />
        )}
        <Kpi
          label="Destino de la rendición"
          tone="celeste"
          icon={<Landmark />}
          value={WHOLESALE_TARGET_LABELS[target]}
          delta={target === 'BRANCH_CASH' ? (branchName ?? '') : 'Lo cambia el dueño en Configuración'}
          deltaMuted
        />
      </div>

      <Card>
        <CardHeader title="Por rendir" subtitle="Lo que cobró cada uno y todavía no entregó." />
        <CardBody>
          {holders.isLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : !holders.data?.length ? (
            <EmptyState icon={<HandCoins />} title="Nadie tiene plata por rendir" />
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {holders.data.map((h) => (
                <div key={h.userId} className="bf-pick" style={{ cursor: 'default', justifyContent: 'space-between' }}>
                  <div>
                    <strong>{h.userName}</strong>
                    <div className="bf-cell-sub">
                      {h.payments.length} {h.payments.length === 1 ? 'cobro' : 'cobros'} · efectivo {formatMoney(h.cashCents)}
                      {h.otherCents > 0 ? ` · otros medios ${formatMoney(h.otherCents)}` : ''}
                    </div>
                  </div>
                  {manage && (
                    <Button variant="primary" onClick={() => setSettling(h)}>
                      Rendir
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {manage && target === 'WHOLESALE_CASH' && (
        <Card>
          <CardHeader
            title="Caja mayorista"
            subtitle="Rendiciones, cobros en efectivo en la oficina, retiros y arqueos."
            actions={
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button onClick={() => setCashMode('DEPOSIT')}>Ingreso</Button>
                <Button onClick={() => setCashMode('WITHDRAWAL')}>Retiro</Button>
                <Button variant="primary" onClick={() => setCashMode('COUNT')}>
                  Arqueo
                </Button>
              </div>
            }
          />
          <CardBody>
            {!cash.data?.entries.length ? (
              <p className="bf-cell-sub">Sin movimientos todavía.</p>
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Movimiento</th>
                      <th style={{ textAlign: 'right' }}>Importe</th>
                      <th style={{ textAlign: 'right' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cash.data.entries.map((e) => (
                      <tr key={e.id}>
                        <td className="bf-cell-sub">{formatDateTime(e.createdAt)}</td>
                        <td>
                          <div className="bf-cell-main">{e.reason}</div>
                          <div className="bf-cell-sub">{e.createdByName}</div>
                        </td>
                        <td style={{ textAlign: 'right', color: e.amountCents < 0 ? 'var(--bf-danger)' : undefined }}>
                          {formatMoney(e.amountCents)}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(e.balanceCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Rendiciones" />
        <CardBody>
          {!settlements.data?.length ? (
            <p className="bf-cell-sub">Todavía no hay rendiciones.</p>
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Quién</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'right' }}>Esperado</th>
                    <th style={{ textAlign: 'right' }}>Entregó</th>
                    <th style={{ textAlign: 'right' }}>Diferencia</th>
                    <th>Entró a</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.data.map((s) => (
                    <tr key={s.id}>
                      <td>{s.number}</td>
                      <td>{s.userName}</td>
                      <td className="bf-cell-sub">{formatDateTime(s.createdAt)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(s.expectedCashCents)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoney(s.receivedCashCents)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {s.differenceCents === 0 ? (
                          <Badge tone="success">Justo</Badge>
                        ) : (
                          <Badge tone={s.differenceCents < 0 ? 'danger' : 'warning'}>{formatMoney(s.differenceCents)}</Badge>
                        )}
                      </td>
                      <td>{s.target === 'BRANCH_CASH' ? `Caja de ${s.branchName ?? 'la sucursal'}` : 'Caja mayorista'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {settling && <SettleDialog holder={settling} onClose={() => setSettling(null)} />}
      {cashMode && <CashDialog mode={cashMode} balance={cash.data?.balanceCents ?? 0} onClose={() => setCashMode(null)} />}
    </>
  );
}

function SettleDialog({ holder, onClose }: { holder: WholesaleHolderDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [received, setReceived] = useState(moneyInput(holder.cashCents));
  const [notes, setNotes] = useState('');
  const got = parseMoney(received);
  const diff = (got ?? 0) - holder.cashCents;
  const save = useMutation({
    mutationFn: () =>
      api.post<WholesaleSettlementDTO>('/wholesale/settlements', { userId: holder.userId, receivedCashCents: got ?? 0, notes: notes || null }),
    onSuccess: async (s) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(`Rendición #${s.number}`, s.differenceCents === 0 ? 'Justo.' : `Diferencia ${formatMoney(s.differenceCents)}.`);
      onClose();
    },
    onError: (e) => toast.error('No se pudo rendir', errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Rendición de ${holder.userName}`} wide>
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Cobro</th>
                <th>Medio</th>
                <th style={{ textAlign: 'right' }}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {holder.payments.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.orderNumber ? `Pedido #${p.orderNumber}` : 'Cuenta corriente'}{' '}
                    <span className="bf-cell-sub">{WHOLESALE_STAGE_LABELS[p.stage]}</span>
                  </td>
                  <td>{WHOLESALE_METHOD_LABELS[p.method]}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(p.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          Efectivo que tiene que entregar: <strong>{formatMoney(holder.cashCents)}</strong>
          {holder.otherCents > 0 && (
            <span className="bf-cell-sub"> · por otros medios ya cobró {formatMoney(holder.otherCents)} (no pasa por la caja)</span>
          )}
        </p>
        <Field label="Efectivo que entrega">
          <Input inputMode="decimal" value={received} onChange={(e) => setReceived(e.target.value)} autoFocus />
        </Field>
        {got !== null && diff !== 0 && (
          <Callout tone={diff < 0 ? 'danger' : 'warning'}>
            {diff < 0 ? `Faltan ${formatMoney(-diff)}.` : `Sobran ${formatMoney(diff)}.`} Queda registrado en la rendición.
          </Callout>
        )}
        <Field label="Notas">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={save.isPending} disabled={got === null} onClick={() => save.mutate()}>
            Confirmar rendición
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CashDialog({ mode, balance, onClose }: { mode: 'WITHDRAWAL' | 'DEPOSIT' | 'COUNT'; balance: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      mode === 'COUNT'
        ? api.post('/wholesale/cash/count', { countedCents: parseMoney(amount) ?? 0, notes: reason || null })
        : api.post('/wholesale/cash/entries', { kind: mode, amountCents: parseMoney(amount) ?? 0, reason }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(mode === 'COUNT' ? 'Arqueo registrado' : 'Movimiento registrado');
      onClose();
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const title =
    mode === 'COUNT' ? 'Arqueo de la caja mayorista' : mode === 'WITHDRAWAL' ? 'Retiro de la caja mayorista' : 'Ingreso a la caja mayorista';
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={title}
      description={mode === 'COUNT' ? `El sistema espera ${formatMoney(balance)}. Contá el efectivo y cargalo.` : undefined}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label={mode === 'COUNT' ? 'Efectivo contado' : 'Importe'}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        <Field label={mode === 'COUNT' ? 'Notas' : 'Motivo'}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} list={mode === 'WITHDRAWAL' ? 'ws-reasons' : undefined} />
        </Field>
        <datalist id="ws-reasons">
          <option value="Depósito al banco" />
          <option value="Pago a proveedor" />
          <option value="Retiro del dueño" />
        </datalist>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={parseMoney(amount) === null || (mode !== 'COUNT' && reason.trim().length < 3)}
            onClick={() => save.mutate()}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
